import React, { useRef, useState } from "react";
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType, TableProps } from 'antd';
import { Button, Input, message, Popconfirm, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./add_model.css";
import axios from "axios";
import useSWR from "swr";

export interface ModelDataType {
    key: string;
    code: string;
    name: string;
}

interface ModelRawDataType {
    id: number;
    code: string;
    description: string;
}

type DataIndex = keyof ModelDataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);

interface AddModelProps {
    onModelSelect: (selectedModel: ModelDataType) => void;
    onCancel: () => void;
}

const AddModel: React.FC<AddModelProps> = ({ onModelSelect, onCancel }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const [editingKey, setEditingKey] = useState<string>('');
    const [editingCode, setEditingCode] = useState<string>('');
    const [editingName, setEditingName] = useState<string>('');
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [newModelCode, setNewModelCode] = useState<string>('');
    const [newModelName, setNewModelName] = useState<string>('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const { data: modelRawData, error: fetchError, mutate } = useSWR<ModelRawDataType[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/models`,
        fetcher
    );

    const models: ModelDataType[] = React.useMemo(() => {
        if (!modelRawData) return [];
        return modelRawData.map(item => ({
            key: item.id.toString(),
            code: item.code,
            name: item.description,
        }));
    }, [modelRawData]);

    const isEditing = (record: ModelDataType) => record.key === editingKey;
    const isAddingRecord = (record: ModelDataType) => record.key === 'new-model';

    const startEdit = (record: ModelDataType) => {
        if (isAdding || selectedRowKeys.length > 0) return;
        setEditingKey(record.key);
        setEditingCode(record.code);
        setEditingName(record.name);
    };

    const cancelEdit = () => {
        setEditingKey('');
        setEditingCode('');
        setEditingName('');
    };

    const saveEdit = async (key: React.Key) => {
        const modelId = parseInt(key.toString(), 10);
        const keyMessage = `update-${key}`;
        message.loading({ content: 'Menyimpan perubahan...', key: keyMessage });

        const codeToSave = editingCode.trim();
        const nameToSave = editingName.trim();

        if (!codeToSave || !nameToSave) {
            message.error({ content: 'Kode dan Nama Model tidak boleh kosong.', key: keyMessage, duration: 3 });
            return;
        }

        try {

            await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/models/${modelId}`, {
                code: codeToSave,
                description: nameToSave
            });
            message.success({ content: 'Perubahan berhasil disimpan!', key: keyMessage, duration: 2 });
            setEditingKey('');
            mutate();
        } catch (err: any) {
            console.error('Gagal menyimpan perubahan:', err);
            message.error({ content: `Gagal menyimpan: ${err.response?.data?.error || err.message || 'Error tidak diketahui'}`, key: keyMessage, duration: 4 });
        }
    };

    const handleAdd = () => {
        if (editingKey) return;
        setIsAdding(true);
        setNewModelCode('');
        setNewModelName('');
        setSelectedRowKeys([]);
    };

    const handleCancelNew = () => {
        setIsAdding(false);
        setNewModelCode('');
        setNewModelName('');
    };

    const handleSaveNew = async () => {
        const codeToAdd = newModelCode.trim();
        const nameToAdd = newModelName.trim();

        if (!codeToAdd || !nameToAdd) {
            message.error('Kode dan Nama Model baru tidak boleh kosong.');
            return;
        }

        const keyMessage = `add-new`;
        message.loading({ content: 'Menyimpan data baru...', key: keyMessage });

        try {
            const payload = {
                code: codeToAdd,
                description: nameToAdd
            };

            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/models`, payload);
            message.success({ content: 'Model baru berhasil ditambahkan!', key: keyMessage, duration: 2 });
            setIsAdding(false);
            setNewModelCode('');
            setNewModelName('');
            mutate();
        } catch (err: any) {
            console.error('Gagal menyimpan model baru:', err);
            message.error({ content: `Gagal menyimpan: ${err.response?.data?.error || err.message || 'Error tidak diketahui'}`, key: keyMessage, duration: 4 });
        }
    };

    const handleDelete = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Tidak ada baris yang dipilih untuk dihapus.');
            return;
        }
        const deleteKey = 'delete-models';
        message.loading({ content: 'Menghapus model...', key: deleteKey });
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/models`, {
                data: { ids: selectedRowKeys.map(key => parseInt(key.toString(), 10)) }
            });
            message.success({ content: `${selectedRowKeys.length} model berhasil dihapus.`, key: deleteKey, duration: 2 });
            mutate();
            setSelectedRowKeys([]);
        } catch (err: any) {
            console.error('Gagal menghapus model:', err);
            message.error({ content: `Gagal menghapus: ${err.response?.data?.error || err.message || 'Error tidak diketahui'}`, key: deleteKey, duration: 4 });
        }
    };

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        if (newSelectedRowKeys.length > 0) {
            if (editingKey) cancelEdit();
            if (isAdding) handleCancelNew();
        }
    };

    const rowSelection: TableProps<ModelDataType>['rowSelection'] = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record) => ({
            disabled: isEditing(record) || isAddingRecord(record),
        }),
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<ModelDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex === 'code' ? 'Code Model' : 'Name Model'}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase())
                : false,
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text, record) => {
            const editing = isEditing(record);
            const adding = isAddingRecord(record);

            if (adding && dataIndex === 'code') {
                return (
                    <Input
                        value={newModelCode}
                        onChange={(e) => setNewModelCode(e.target.value)}
                        onPressEnter={handleSaveNew}
                        placeholder="Kode Model Baru"
                        style={{ margin: '-5px 0' }}
                    />
                );
            }
            if (adding && dataIndex === 'name') {
                return (
                    <Input
                        value={newModelName}
                        onChange={(e) => setNewModelName(e.target.value)}
                        onPressEnter={handleSaveNew}
                        placeholder="Nama Model Baru"
                        style={{ margin: '-5px 0' }}
                    />
                );
            }

            if (editing && dataIndex === 'code') {
                return (
                    <Input
                        value={editingCode}
                        onChange={(e) => setEditingCode(e.target.value)}
                        style={{ margin: '-5px 0' }}
                        onPressEnter={() => saveEdit(record.key)}
                    />
                );
            }
            if (editing && dataIndex === 'name') {
                return (
                    <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        style={{ margin: '-5px 0' }}
                        onPressEnter={() => saveEdit(record.key)}
                    />
                );
            }
            return searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            );
        },
    });

    const columns: TableColumnsType<ModelDataType> = [
        {
            title: 'Code Model',
            dataIndex: 'code',
            key: 'code',
            width: '40%',
            ...getColumnSearchProps('code'),
        },
        {
            title: 'Name Model',
            dataIndex: 'name',
            key: 'name',
            width: '60%',
            ...getColumnSearchProps('name'),
        },
        {
            title: 'Action',
            key: 'action',
            width: '30%', // +++ Menyesuaikan lebar kolom
            render: (_, record) => {
                const editing = isEditing(record);
                const adding = isAddingRecord(record);

                // +++ Tampilkan tombol Save dan Cancel untuk baris tambah baru
                if (adding) {
                    return (
                        <Space size="middle">
                            <Button type="link" icon={<CheckOutlined />} onClick={handleSaveNew} style={{ padding: 0, color: 'green' }} />
                            <Button type="link" icon={<CloseOutlined />} onClick={handleCancelNew} style={{ padding: 0, color: 'red' }} />
                        </Space>
                    );
                }

                // *** Tampilkan tombol Save dan Cancel untuk baris yang sedang diedit
                if (editing) {
                    return (
                        <Space size="middle">
                            {/* *** Popconfirm untuk konfirmasi simpan */}
                            <Popconfirm title="Simpan perubahan?" onConfirm={() => saveEdit(record.key)} okText="Ya" cancelText="Tidak">
                                <Button type="link" icon={<CheckOutlined />} style={{ padding: 0, color: 'green' }} />
                            </Popconfirm>
                            <Button
                                type="link"
                                icon={<CloseOutlined />}
                                onClick={cancelEdit}
                                style={{ padding: 0, color: 'red' }}
                            />
                        </Space>
                    );
                }

                // *** Tampilkan tombol Edit dan Delete untuk baris normal
                return (
                    <Space size="middle">
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => startEdit(record)}
                            style={{ padding: 0 }}
                            // *** Nonaktifkan tombol edit jika ada baris lain yang sedang diedit/ditambah atau ada baris terpilih
                            disabled={editingKey !== '' || isAdding || selectedRowKeys.length > 0}
                        />
                        {/* --- Popconfirm untuk konfirmasi hapus satu baris (opsional, bisa hanya pakai delete selected) */}
                        {/* <Popconfirm
                            title={`Hapus model "${record.name}"?`}
                            onConfirm={() => handleDelete([record.key])} // Panggil handleDelete dengan array berisi key baris ini
                            okText="Ya"
                            cancelText="Tidak"
                            disabled={editingKey !== '' || isAdding || selectedRowKeys.length > 0}
                        >
                             <Button
                                type="link"
                                icon={<DeleteOutlined />}
                                style={{ padding: 0, color: 'red' }}
                                disabled={editingKey !== '' || isAdding || selectedRowKeys.length > 0}
                            />
                        </Popconfirm> */}
                    </Space>
                );
            },
        },
    ];

    const displayData = isAdding
        ? [{ key: 'new-model', code: '', name: '' }, ...models]
        : models;
    const handleRowDoubleClick = (record: ModelDataType) => {
        if (isAddingRecord(record) || isEditing(record) || editingKey !== '' || isAdding) {
            return;
        }
        console.log("Double clicked row:", record);
        onModelSelect(record);
        onCancel();
    };


    return (

        <div id="iklk" style={transformStyle}>
            <div className="gjs-grid-row" id="itri1">
                <div className="gjs-grid-column" id="ikwc7">
                    <Space style={{ marginBottom: 16 }}>
                        <Button
                            onClick={handleAdd}
                            type="primary"
                            icon={<PlusOutlined />}
                            disabled={isAdding || editingKey !== '' || selectedRowKeys.length > 0}
                        >
                            Add Model
                        </Button>
                        <Popconfirm
                            title={`Hapus ${selectedRowKeys.length} model terpilih?`}
                            description="Pastikan model tidak sedang digunakan." 
                            onConfirm={handleDelete}
                            okText="Ya, Hapus"
                            cancelText="Tidak"
                            disabled={selectedRowKeys.length === 0 || isAdding || editingKey !== ''}
                        >
                            <Button
                                type="primary"
                                danger
                                icon={<DeleteOutlined />}
                                disabled={selectedRowKeys.length === 0 || isAdding || editingKey !== ''}
                            >
                                Delete Selected
                            </Button>
                        </Popconfirm>
                    </Space>
                    {fetchError && <div style={{ color: 'red', marginBottom: '16px' }}>Error: Gagal memuat data model. {fetchError.message}</div>}
                    <Table<ModelDataType>
                        columns={columns}
                        dataSource={displayData}
                        pagination={{ pageSize: 5 }}
                        loading={!modelRawData && !fetchError && !isAdding}
                        rowKey="key"
                        rowSelection={rowSelection}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    if (!isEditing(record) && !isAddingRecord(record) && editingKey === '' && !isAdding) {
                                        handleRowDoubleClick(record);
                                    }
                                },
                            };
                        }}
                        rowClassName={(record) => {
                            if (isEditing(record)) return 'editable-row';
                            if (isAddingRecord(record)) return 'adding-row';
                            return '';
                        }}
                        bordered 
                        size="small" 
                        style={{ marginTop: '20px' }} 
                    />
                </div>
            </div>
        </div>
    );
};

export default AddModel;